import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Search, Mail } from "lucide-react";
import { format } from "date-fns";

interface BetaLead {
  id: string;
  created_at: string;
  name: string;
  email: string;
  phone: string | null;
  shop_name: string;
  shop_size: number | null;
  role: string;
  country: string | null;
  timezone: string | null;
  notes: string | null;
  status: 'pending' | 'contacted' | 'onboarded' | 'declined';
  contacted_at: string | null;
  source: string;
  utm_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
}

export default function BetaLeads() {
  const { membership } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState<BetaLead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<BetaLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  const sendBetaInvite = async (lead: BetaLead) => {
    setSendingInvite(lead.id);
    try {
      const { data, error } = await supabase.functions.invoke('send-beta-invite', {
        body: {
          email: lead.email,
          full_name: lead.name,
          shop_name: lead.shop_name,
          phone: lead.phone,
          role_requested: lead.role,
          shop_size: lead.shop_size,
          country: lead.country,
          timezone: lead.timezone,
        }
      });

      if (error) throw error;

      toast.success(`Invite sent to ${lead.email}`);
      await updateStatus(lead.id, 'contacted');
    } catch (err: any) {
      console.error('Error sending invite:', err);
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setSendingInvite(null);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from('beta_leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeads(data as BetaLead[] || []);
      setFilteredLeads(data as BetaLead[] || []);
    } catch (error: any) {
      console.error('Error fetching beta leads:', error);
      toast.error('Failed to load beta leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = leads;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.shop_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, leads]);

  const updateStatus = async (id: string, newStatus: BetaLead['status']) => {
    try {
      const updates: any = { status: newStatus };
      if (newStatus === 'contacted') {
        updates.contacted_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('beta_leads')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast.success('Status updated');
      fetchLeads();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Shop Name', 'Shop Size', 'Role', 'Country', 'Timezone', 'Status', 'Notes', 'Source', 'UTM Campaign', 'UTM Source', 'UTM Medium'];
    const rows = filteredLeads.map(lead => [
      format(new Date(lead.created_at), 'yyyy-MM-dd HH:mm'),
      lead.name,
      lead.email,
      lead.phone || '',
      lead.shop_name,
      lead.shop_size || '',
      lead.role,
      lead.country || '',
      lead.timezone || '',
      lead.status,
      lead.notes || '',
      lead.source,
      lead.utm_campaign || '',
      lead.utm_source || '',
      lead.utm_medium || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beta-leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('CSV exported');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'default';
      case 'contacted': return 'secondary';
      case 'onboarded': return 'default';
      case 'declined': return 'destructive';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <p>Loading beta leads...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Beta Sign-Ups</h1>
          <p className="text-muted-foreground mt-1">
            Manage beta access requests from the marketing site
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and filter beta leads</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or shop name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="onboarded">Onboarded</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Leads ({filteredLeads.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      No beta leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(lead.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {lead.email}
                        </a>
                      </TableCell>
                      <TableCell>{lead.shop_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{lead.role}</Badge>
                      </TableCell>
                      <TableCell>{lead.shop_size ? `${lead.shop_size} bays` : '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {lead.country || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(lead.status)}>
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="flex items-center gap-2">
                        {(lead.status === 'pending' || lead.status === 'contacted') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendBetaInvite(lead)}
                            disabled={sendingInvite === lead.id}
                          >
                            {sendingInvite === lead.id ? 'Sending...' : 'Send Invite'}
                          </Button>
                        )}
                        <Select
                          value={lead.status}
                          onValueChange={(value) => updateStatus(lead.id, value as BetaLead['status'])}
                        >
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="onboarded">Onboarded</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
